import { useMemo } from 'react';
import { FolderKanban } from 'lucide-react';
import { ViewBar } from './ViewBar';
import {
  getTaskViews,
  getView,
  getDefaultViewId,
  isValidViewId,
  isTaskViewId,
  isChatViewId,
  type ViewId,
  type TaskViewId,
  type ViewProps,
} from './ViewRegistry';
import { ProjectChatView } from '@/components/chats';
import type { Task, TaskStatus, ProjectMember, ProjectChat } from '@/types';
import type { TaskVisibilityScope } from '@/lib/api';

// Import views to register them
import '@/components/views/BoardView';
import '@/components/views/ListView';
import '@/components/views/WorkloadView';

interface TaskWorkspaceProps {
  projectId: string;
  listId: string;
  tasks: Task[];
  statuses: TaskStatus[];
  members: ProjectMember[];
  isLoading: boolean;
  isClient: boolean;
  currentView: string;
  onViewChange: (viewId: ViewId) => void;
  taskVisibilityScope: TaskVisibilityScope;
  onTaskVisibilityScopeChange: (scope: TaskVisibilityScope) => void;
  showTaskVisibilityFilter: boolean;
  chats?: ProjectChat[];
}

export function TaskWorkspace({
  projectId,
  listId,
  tasks,
  statuses,
  members,
  isLoading,
  isClient,
  currentView,
  onViewChange,
  taskVisibilityScope,
  onTaskVisibilityScopeChange,
  showTaskVisibilityFilter,
  chats = [],
}: TaskWorkspaceProps) {
  const views = useMemo(() => getTaskViews(), []);

  const viewId: ViewId = isValidViewId(currentView) ? currentView : getDefaultViewId();

  // Check if this is a chat view
  const isChatView = isChatViewId(viewId);

  // Find the chat for chat views
  const activeChat = isChatView
    ? chats.find((c) =>
        viewId === 'internal-chat' ? c.type === 'internal' : c.type === 'client'
      )
    : null;

  // For task views, get the registered view component
  const activeView = isTaskViewId(viewId) ? getView(viewId) : null;

  const viewProps: ViewProps = {
    projectId,
    listId,
    tasks,
    statuses,
    members,
    isLoading,
    isClient,
  };

  // Render chat view
  if (isChatView) {
    if (!activeChat) {
      return (
        <div className="flex h-full flex-col">
          <ViewBar
            currentView={viewId}
            onViewChange={onViewChange}
            views={views.map((v) => ({ id: v.id as TaskViewId, label: v.label }))}
            chats={chats}
            isClient={isClient}
            taskVisibilityScope={taskVisibilityScope}
            onTaskVisibilityScopeChange={onTaskVisibilityScopeChange}
            showTaskVisibilityFilter={showTaskVisibilityFilter}
          />
          <div className="flex h-full flex-col items-center justify-center gap-4">
            <p className="text-muted-foreground">Chat not available</p>
          </div>
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col">
        <ViewBar
          currentView={viewId}
          onViewChange={onViewChange}
          views={views.map((v) => ({ id: v.id as TaskViewId, label: v.label }))}
          chats={chats}
          isClient={isClient}
          taskVisibilityScope={taskVisibilityScope}
          onTaskVisibilityScopeChange={onTaskVisibilityScopeChange}
          showTaskVisibilityFilter={showTaskVisibilityFilter}
        />
        <div className="flex-1 overflow-hidden">
          <ProjectChatView chat={activeChat} projectId={projectId} />
        </div>
      </div>
    );
  }

  // Render task view
  if (!activeView) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <FolderKanban className="h-12 w-12 text-muted-foreground/50" />
        <p className="text-muted-foreground">View not found</p>
      </div>
    );
  }

  const ViewComponent = activeView.component;

  return (
    <div className="flex h-full flex-col">
      <ViewBar
        currentView={viewId}
        onViewChange={onViewChange}
        views={views.map((v) => ({ id: v.id as TaskViewId, label: v.label }))}
        chats={chats}
        isClient={isClient}
        taskVisibilityScope={taskVisibilityScope}
        onTaskVisibilityScopeChange={onTaskVisibilityScopeChange}
        showTaskVisibilityFilter={showTaskVisibilityFilter}
      />
      <div className="flex-1 overflow-hidden">
        <ViewComponent {...viewProps} />
      </div>
    </div>
  );
}
